import { localCalendar, type Calendar } from './calendar.js';
import { DAY_MS } from './reminders.js';
import { MAX_DONE_LOG, type Card, type CardFields, type Repeat } from './types.js';

const startOfDay = localCalendar.startOfDay;
const addDays = localCalendar.addDays;

/** ISO weekday, 1 Monday to 7 Sunday. */
function isoWeekday(ts: number): number {
  const w = new Date(ts).getDay();
  return w === 0 ? 7 : w;
}

/** A due date at local midnight is a date alone; anything else carries a time to keep. */
function timeOf(ts: number | null): { h: number; m: number } | null {
  if (ts === null) return null;
  const d = new Date(ts);
  return d.getHours() === 0 && d.getMinutes() === 0 ? null : { h: d.getHours(), m: d.getMinutes() };
}

function atTime(dayTs: number, time: { h: number; m: number } | null): number {
  if (!time) return dayTs;
  const d = new Date(dayTs);
  d.setHours(time.h, time.m, 0, 0);
  return d.getTime();
}

function monthlyOn(year: number, month: number, day: number): number {
  const last = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(day, last)).getTime();
}

/**
 * The next due date once a recurring card is done at `now`. Fixed rules jump to the first
 * occurrence strictly after today, so missed weeks never stack up; the interval rule counts
 * from today. The time of day of the previous due date survives. Ongoing cards have no next.
 */
export function nextDue(repeat: Repeat, previousDue: number | null, now: number): number | null {
  const today = startOfDay(now);
  const time = timeOf(previousDue);
  switch (repeat.kind) {
    case 'after':
      return atTime(addDays(today, repeat.days), time);
    case 'weekly': {
      for (let i = 1; i <= 7; i++) {
        const day = addDays(today, i);
        if (repeat.days.includes(isoWeekday(day))) return atTime(day, time);
      }
      return null;
    }
    case 'monthly': {
      const d = new Date(today);
      const thisMonth = monthlyOn(d.getFullYear(), d.getMonth(), repeat.day);
      return atTime(thisMonth > today ? thisMonth : monthlyOn(d.getFullYear(), d.getMonth() + 1, repeat.day), time);
    }
    case 'yearly': {
      const d = new Date(today);
      const thisYear = monthlyOn(d.getFullYear(), repeat.month - 1, repeat.day);
      return atTime(thisYear > today ? thisYear : monthlyOn(d.getFullYear() + 1, repeat.month - 1, repeat.day), time);
    }
    case 'ongoing':
      return null;
  }
}

/**
 * The due date to give a card that gets a dated rule while it has none: today when today fits
 * the rule (an interval always starts today), else the next occurrence.
 */
export function firstDue(repeat: Exclude<Repeat, { kind: 'ongoing' }>, now: number): number {
  const today = startOfDay(now);
  if (repeat.kind === 'after') return today;
  if (repeat.kind === 'weekly' && repeat.days.includes(isoWeekday(today))) return today;
  if (repeat.kind === 'monthly') {
    const d = new Date(today);
    if (monthlyOn(d.getFullYear(), d.getMonth(), repeat.day) === today) return today;
  }
  if (repeat.kind === 'yearly') {
    const d = new Date(today);
    if (monthlyOn(d.getFullYear(), repeat.month - 1, repeat.day) === today) return today;
  }
  return nextDue(repeat, null, now) ?? today;
}

/** When a resting card returns to the board: the start of the day `lead` days before it is due. */
export function wakeAt(card: Pick<Card, 'repeat' | 'dueAt'>, cal: Calendar = localCalendar): number | null {
  if (!card.repeat || card.repeat.kind === 'ongoing' || card.dueAt === null) return null;
  return cal.addDays(cal.startOfDay(card.dueAt), -card.repeat.lead);
}

/** Between two occurrences the card rests off the board. */
export function isResting(card: Pick<Card, 'repeat' | 'dueAt'>, now: number, cal: Calendar = localCalendar): boolean {
  const wake = wakeAt(card, cal);
  return wake !== null && wake > now;
}

/** The last time an ongoing card was touched, its creation counting as the first touch. */
export function lastTouch(card: Pick<Card, 'touchedAt' | 'createdAt'>): number {
  return card.touchedAt ?? card.createdAt;
}

/** An ongoing card past its rhythm has gone quiet. */
export function isQuiet(card: Pick<Card, 'repeat' | 'touchedAt' | 'createdAt'>, now: number): boolean {
  if (card.repeat?.kind !== 'ongoing') return false;
  return now - lastTouch(card) > card.repeat.days * DAY_MS;
}

/**
 * What Done means on a recurring card, or Touch on an ongoing one: the occurrence is logged,
 * the card counts as touched, and a dated rule moves the due date on. The card stays open.
 */
export function completeOnce(card: Pick<Card, 'repeat' | 'dueAt' | 'doneLog'>, now: number): Partial<CardFields> {
  const doneLog = [...card.doneLog, now].slice(-MAX_DONE_LOG);
  const changes: Partial<CardFields> = { doneLog, touchedAt: now };
  if (card.repeat && card.repeat.kind !== 'ongoing') changes.dueAt = nextDue(card.repeat, card.dueAt, now);
  return changes;
}

/** Undo of completeOnce: the previous due date and log come back. */
export function uncompleteOnce(before: Pick<Card, 'dueAt' | 'doneLog' | 'touchedAt'>): Partial<CardFields> {
  return { dueAt: before.dueAt, doneLog: before.doneLog, touchedAt: before.touchedAt };
}
